// src/pages/Admin/Orders.js
import React, { useState, useEffect } from 'react';
import { supabase } from '../../services/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Box, Typography, CircularProgress, Paper, Button,
  Divider, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Snackbar, Alert, useTheme, useMediaQuery
} from '@mui/material';
import Edit from '@mui/icons-material/Edit';
import MobileOrdersView from '../../components/Orders/Admin/MobileOrdersView';
import DesktopOrdersView from '../../components/Orders/Admin/DesktopOrdersView';

const AdminOrders = () => {
  const { isAdmin } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    orderId: null,
    status: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const { data } = await supabase
          .from('orders')
          .select(`
            *,
            profiles:user_id (full_name, phone_number),
            order_items(
              *,
              products(*)
            )
          `)
          .order('created_at', { ascending: false });

        setOrders(data || []);
      } catch (error) {
        console.error('Orders error:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isAdmin) fetchOrders();
  }, [isAdmin]);

  const handleStatusUpdate = async (orderId, newStatus) => {
    // If changing to cancelled status, open confirmation dialog
    if (newStatus === 'cancelled') {
      setConfirmDialog({
        open: true,
        orderId,
        status: newStatus
      });
      return;
    }
    
    // Otherwise proceed with the update
    await updateOrderStatus(orderId, newStatus);
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      // First, get order details to process stock updates if needed
      const { data: orderDetails } = await supabase
        .from('orders')
        .select(`
          status,
          order_items(
            product_color_id,
            size,
            quantity
          )
        `)
        .eq('id', orderId)
        .single();

      // Update the order status
      await supabase
        .from('orders')
        .update({ status: newStatus, updated_at: new Date() })
        .eq('id', orderId);

      // Handle stock updates when cancelling an order (unless already cancelled)
      if (newStatus === 'cancelled' && orderDetails.status !== 'cancelled') {
        // Increase stock for each item in the order
        for (const item of orderDetails.order_items) {
          const { error: increaseStockError } = await supabase.rpc('increase_product_stock', {
            p_product_color_id: item.product_color_id,
            p_size: item.size,
            p_quantity: item.quantity
          });

          if (increaseStockError) {
            throw new Error(`Failed to update stock: ${increaseStockError.message}`);
          }
        }
      }

      // Update the orders state
      setOrders(orders.map(order => 
        order.id === orderId ? { ...order, status: newStatus } : order
      ));

      // Show success message
      setSnackbar({
        open: true,
        message: 'تم تحديث حالة الطلب بنجاح',
        severity: 'success'
      });
      
    } catch (error) {
      console.error('Update status error:', error);
      setSnackbar({
        open: true,
        message: `حدث خطأ: ${error.message}`,
        severity: 'error'
      });
    } finally {
      // Close dialog if it was open
      setConfirmDialog({
        open: false,
        orderId: null,
        status: ''
      });
    }
  };

  const handleCloseDialog = () => {
    setConfirmDialog({
      open: false,
      orderId: null,
      status: ''
    });
  };

  const handleConfirmCancel = () => {
    updateOrderStatus(confirmDialog.orderId, confirmDialog.status);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'confirmed':
        return 'primary';
      case 'shipped':
        return 'info';
      default:
        return 'warning'; // For 'pending' and any other status
    }
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      'pending': 'قيد الانتظار',
      'confirmed': 'تم التأكيد',
      'shipped': 'تم الشحن',
      'delivered': 'تم التسليم',
      'cancelled': 'ملغي'
    };
    return statusMap[status] || status;
  };

  if (!isAdmin) return <Typography>غير مصرح بالوصول</Typography>;
  
  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Box sx={{ 
        bgcolor: 'primary.main', 
        color: 'white', 
        p: 2, 
        borderRadius: 1,
        mb: 3,
        mx: { xs: 0.5, md: 1 }, // Responsive negative horizontal margins
        mt: { xs: 1, md: 2 }  // Responsive negative top margin
      }}>
        <Typography variant="h6" sx={{ fontWeight: 700, textAlign: 'center' }}>
          إدارة الطلبات
        </Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />

      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
        {orders.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Edit sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              لا توجد طلبات
            </Typography>
          </Box>
        ) : (
          <>
            {/* Conditional rendering based on screen size */}
            {isMobile ? (
              <MobileOrdersView 
                orders={orders}
                handleStatusUpdate={handleStatusUpdate}
                getStatusColor={getStatusColor}
                getStatusLabel={getStatusLabel}
              />
            ) : (
              <DesktopOrdersView 
                orders={orders}
                handleStatusUpdate={handleStatusUpdate}
                getStatusColor={getStatusColor}
                getStatusLabel={getStatusLabel}
              />
            )}
          </>
        )}
      </Paper>

      {/* Confirmation Dialog for Cancellation */}
      <Dialog
        open={confirmDialog.open}
        onClose={handleCloseDialog}
      >
        <DialogTitle>تأكيد إلغاء الطلب</DialogTitle>
        <DialogContent>
          <DialogContentText>
            هل أنت متأكد من رغبتك في إلغاء هذا الطلب؟ سيتم إعادة الكميات إلى المخزون.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            إلغاء
          </Button>
          <Button onClick={handleConfirmCancel} color="error" autoFocus>
            تأكيد الإلغاء
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminOrders;